import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * PortfolioPage component
 * Displays a comprehensive portfolio of translation projects
 * Supports multiple languages through i18n
 */
const PortfolioPage = () => {
  const { t } = useTranslation();
  // Scroll to anchor on load if present in URL
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Remove the # symbol
      const id = hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        // Use setTimeout to ensure the page has fully loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  // Portfolio items - expanded from the homepage section
  const portfolioItems = [
    {
      id: "carried-away",
      title: "Carried Away",
      description: "A physics-based puzzle game involving chairlifts and jumps. We provided complete French localization for the game, including UI elements, tutorial text, and marketing materials.",
      image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/09/logocarried.png",
      details: "Carried Away is a physics-based construction puzzle game where players build chairlifts, gondolas, and jumps to help skiers reach the finish line. We worked closely with Huge Calf Studios to ensure all game text was accurately translated while maintaining the game's humorous tone. The project included translating over 5,000 words of in-game text, UI elements, and Steam store page content.",
      client: "Huge Calf Studios",
      year: "2017",
      link: "https://store.steampowered.com/app/615880/Carried_Away/"
    },
    {
      id: "startup-company",
      title: "Startup Company",
      description: "A business simulation game where players build and expand a startup.",
      image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/08/logo.png",
      details: "Startup Company is a business simulation game where players build and grow their own tech company. We provided ongoing translation services as the game developed through Early Access, ensuring French players could enjoy new content as it was released. The project involved translating technical business terminology while keeping it accessible to players.",
      client: "Jonas Hovgaard",
      year: "2017-2018",
      link: "https://store.steampowered.com/app/606800/Startup_Company/"
    },
    {
      id: "spacejacked",
      title: "SpaceJacked",
      description: "A fast-paced mix of tower defense and shooting.",
      image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=SpaceJacked",
      details: "SpaceJacked is an action-packed tower defense game with shooting elements. We translated all in-game dialogue, character interactions, and UI elements, ensuring the fast-paced nature of the game was preserved in the French version. Special attention was paid to maintaining the game's unique character voices and personality.",
      client: "Rotten Mage",
      year: "2016",
      link: "https://store.steampowered.com/app/390680/SpaceJacked/"
    },
    {
      id: "archaica",
      title: "Archaica: The Path of Light",
      description: "A mystical puzzle game featuring lasers and mirrors.",
      image: "https://nativetranslationsfr.wordpress.com/wp-content/uploads/2017/09/header1.jpg",
      details: "Archaica: The Path of Light is a beautiful puzzle game where players manipulate mirrors, prisms, and other devices to direct beams of light. We translated the game's mystical storyline, puzzle instructions, and UI elements, ensuring the atmospheric and immersive quality of the game was maintained in French. The project required careful attention to the game's unique terminology and poetic language.",
      client: "TwoMammoths",
      year: "2017",
      link: "https://store.steampowered.com/app/550590/Archaica_The_Path_of_Light/"
    },
    {
      id: "blobcat",
      title: "BlobCat",
      description: "A cute puzzle game featuring blob-shaped cats.",
      image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=BlobCat",
      details: "BlobCat is a charming puzzle game where players guide cute blob-shaped cats to safety. We translated the game's playful dialogue, character names, and puzzle instructions, ensuring the game's whimsical tone was preserved in French. The project involved creating cat-related wordplay that would resonate with French-speaking players.",
      client: "Christian Wasser",
      year: "2018",
      link: "#"
    },
    {
      id: "elementals-reborn",
      title: "Elementals Reborn",
      description: "A strategic card game with elemental powers.",
      image: "https://via.placeholder.com/600x400/000000/FFFFFF?text=Elementals+Reborn",
      details: "Elementals Reborn is a strategic card game where players harness the power of different elements. We translated all card text, game rules, and UI elements, ensuring the technical terminology was accurately conveyed while maintaining the game's fantasy atmosphere. Special attention was paid to consistency in elemental terminology throughout the game.",
      client: "Elementals Reborn Team",
      year: "2019",
      link: "#"
    }
  ];

  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Portfolio | NatuXtranslations</title>
        <meta name="description" content="Explore our portfolio of French game localization projects. We've helped indie and established developers bring their games to French-speaking audiences." />
        <meta name="keywords" content="french game translation portfolio, game localization examples, french translation projects" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-32 bg-bg-alt relative">
        <div className="container mx-auto px-6">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={titleVariants}
          >
            <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
            <h1 className="font-heading text-4xl md:text-6xl font-normal mb-8 tracking-wide">
              {t('portfolio.title')}
            </h1>
            <p className="text-light text-lg md:text-xl font-light leading-relaxed">
              {t('portfolio.description')}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Portfolio Items */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="space-y-32">
            {portfolioItems.map((item, index) => (
              <PortfolioItem 
                key={index}
                item={item}
                isEven={index % 2 === 0}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 bg-primary text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-normal mb-8 tracking-wide">
            {t('portfolio.readyToLocalize')}
          </h2>
          <p className="text-xl text-white/80 font-light mb-12 max-w-2xl mx-auto">
            {t('portfolio.workTogether')}
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-white text-primary px-10 py-4 uppercase tracking-widest text-sm font-light transition-all duration-300 hover:bg-white/90 hover:translate-y-[-2px]"
          >
            {t('portfolio.getInTouch')}
          </Link>
        </div>
      </section>
    </>
  );
};

/**
 * PortfolioItem component
 * Displays a single portfolio project with details
 */
const PortfolioItem = ({ item, isEven }) => {
  const { t } = useTranslation();
  const itemRef = useRef(null);
  const isInView = useInView(itemRef, { once: true, amount: 0.2 });
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <motion.div 
      id={item.id}
      ref={itemRef}
      className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      {/* Image */}
      <div className="w-full lg:w-1/2">
        <div className="relative overflow-hidden border border-primary/10">
          <img 
            src={item.image} 
            alt={item.title} 
            className="w-full h-auto object-cover aspect-video"
          />
        </div>
      </div>
      
      {/* Content */}
      <div className="w-full lg:w-1/2">
        <h2 className="font-heading text-3xl mb-6 tracking-wide">{item.title}</h2>
        <p className="text-light text-lg font-light mb-8 leading-relaxed">
          {item.details}
        </p>
        
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="text-sm uppercase tracking-wider text-primary/60 mb-2">{t('portfolio.client')}</h3>
            <p className="font-medium">{item.client}</p>
          </div>
          <div>
            <h3 className="text-sm uppercase tracking-wider text-primary/60 mb-2">{t('portfolio.year')}</h3>
            <p className="font-medium">{item.year}</p>
          </div>
        </div>
        
        {item.link && (
          <a 
            href={item.link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-primary font-light tracking-wider hover:text-secondary transition-colors duration-300"
          >
            {t('portfolio.viewOnSteam')}
            <svg className="w-4 h-4 ml-3 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
            </svg>
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default PortfolioPage;
